/**
 * The page and conversation side of `InsertionSlot`: reads the paragraphs under
 * an anchor on the live page, names one that has no paragraph bookmark, finds in
 * the conversation's turns every card that adds a block after an anchor, and
 * settles each shared slot's order through the core's order step.
 *
 * SLOT
 *   Every card of the conversation that adds a block after one anchor, applied or
 *   not. A block is a new paragraph (`insertions`) or a new table
 *   (`tableChanges.tableInsertions`); both name the paragraph they follow, so both
 *   belong to the slot. A slot with one card needs no order; a slot with two or
 *   more has each card ranked once by the order step, which reads what the blocks
 *   say.
 *
 * RANK
 *   Stored with the conversation on the card's tool result turn
 *   (`insertion_slot_ranks`, by anchor), so every apply on every device reads the
 *   same order. A card not ranked yet is ordered before its slot is placed: the
 *   slot's unranked cards one at a time, in the order the agent proposed them,
 *   each against the cards ranked before it.
 *
 * LANDING
 *   Where a card's block would go if it were applied now: after the anchor, or
 *   after the last block of its slot already on the page that ranks before it.
 *   Its card shows it, so the paragraph a card names as the one it follows moves
 *   on as the cards before it are applied.
 *
 * PLACE
 *   Where Go to issue takes the user for a card of a slot: an applied card's own
 *   block, where it was written; an open card's landing, the block its "After"
 *   line names; the anchor when neither stands on the page.
 *
 * CALL INDEX
 *   Where the card's tool call sits among the conversation's cards, carried beside
 *   the rank. It orders a slot the order step never settled, so such a slot reads
 *   in the order the agent proposed its cards instead of in reverse of the order
 *   they were applied in.
 *
 * CARD ID
 *   A turn belongs to the card named by its tool call id, or by the turn's own
 *   id when the artifacts carry none, as `getActionIdentifier` reads it; the
 *   applied markers are keyed the same way.
 */
import { type CardInsertionOrder, compareSlotOrder, InsertionSlot, type SlotInsertion, type SlotMember, type SlotOrder, type SlotPage, type SlotParagraph } from "@common/utils/redline/insertion-slot";
import { freshParagraphBookmarkName, isParagraphBookmarkName } from "@common/utils/redline/ooxml/paragraph-bookmark-naming";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { z } from "zod";

import { proposalCardContentOf } from "../components/chat/proposal-card/card-content";
import { isToolErrorResult } from "../types/chat";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import type { LiveReviewBulkApplyRemedy, OrderInsertionRequest, SlotPlacementResponse } from "./api";
import { withWordContextRead, withWordContextWrite } from "./document";
import type { ConversationMessage } from "./web-api";

/**
 * A card's new blocks after one anchor, and the anchor's text as the card read
 * it: the paragraphs it adds there, and the tables it adds there as their rows.
 */
export interface SlotCardInsertion {
    anchorBookmark: string;
    anchorText: string;
    paragraphTexts: string[];
    tableRows: string[][][];
}

/**
 * A card that adds blocks: its id, the agent turn it came in (counted by the
 * user turns before it), where its tool call sits among the conversation's
 * cards, why it was proposed, and its blocks by anchor.
 *
 * WHY the call index
 *   It orders a slot whose cards the order step has not settled, so such a slot
 *   still reads in the order the agent proposed its cards instead of in reverse
 *   of the order they were applied in.
 */
export interface SlotCard {
    cardId: string;
    agentTurnNumber: number;
    callIndex: number;
    intent: string;
    insertions: SlotCardInsertion[];
}

/** A card's stored place in one slot. */
export interface StoredSlotRank {
    rank: number;
    reason: string;
}

/** Asks the core's order step for one arriving card's place in its slot. */
export type InsertionOrderer = (request: OrderInsertionRequest) => Promise<SlotPlacementResponse>;

/** Told which card's order or placement failed and why; the card takes the end of its slot, or is written under its anchor as before. */
export type PlacementFailureReporter = (cardId: string, failure: unknown) => void;

/** What ordering a conversation's slots needs: the conversation, its turns, the page, the order step, and where failures go. */
export interface SlotOrderingInput {
    conversationId: string;
    turns: ConversationMessage[];
    page: SlotPage;
    orderInsertion: InsertionOrderer;
    reportFailure: PlacementFailureReporter;
}

/**
 * The slots one apply touches: the anchors its cards insert after, and the
 * order step when there is a signed-in user to ask it as.
 *
 * NULL
 *   `orderInsertion` is null when no user is signed in: the slots keep the ranks
 *   the conversation holds.
 */
export interface SlotsToOrder extends Omit<SlotOrderingInput, "orderInsertion"> {
    anchorBookmarks: string[];
    orderInsertion: InsertionOrderer | null;
}

/** The card id a failure to order whole slots is reported under: it belongs to no one card. */
export const SLOT_ORDERING_FAILURE_CARD_ID = "slot-ordering";

/** The tools whose result is a card that can land a paragraph. */
const LANDING_TOOL_NAMES = new Set(["redline_section", "redline_selection", "edit_table", "add_paragraph"]);

/** The turn artifact the core's order step stores a card's ranks under. */
const INSERTION_SLOT_RANKS_KEY = "insertion_slot_ranks";

const storedSlotRanksSchema = z.record(z.string(), z.object({ rank: z.number(), reason: z.string() }));

const regenerateContextSchema = z.object({ regenerate_context: z.object({ instructions: z.string() }) });

/** Ranks the order step returned in this session, by conversation, card and anchor, until the turns carry them. */
const ranksOrderedThisSession = new Map<string, Map<string, Map<string, StoredSlotRank>>>();

/** The ordering of one slot under way, by conversation and anchor, so two applies order a slot once. */
const slotOrderingsInFlight = new Map<string, Promise<void>>();

/**
 * The slots the order step is ranking cards in right now, by conversation and
 * anchor: what a card reads to show that its Apply waits on that ranking.
 *
 * WHY a count and not a flag
 *   A slot stays shown as ranking until every ranking of it under way has
 *   finished, whichever finishes first.
 */
const slotRankingsUnderWay = new Map<string, number>();

/** Told whenever a slot's ranking starts or finishes, or a rank is stored. */
const slotRankingListeners = new Set<() => void>();

/** Counts the changes the listeners were told of, so a reader can tell a change from a re-read. */
let slotRankingChangeCount = 0;

/** The key of one slot of one conversation in the maps that track its ordering. */
function slotKeyOf(conversationId: string, anchorBookmark: string): string {
    return `${conversationId}\u0000${anchorBookmark}`;
}

/** Tells every listener that a slot's ranking started or finished, or that a rank was stored. */
function announceSlotRankingChange(): void {
    slotRankingChangeCount += 1;
    for (const listener of slotRankingListeners) listener();
}

/**
 * Marks one slot as being ranked, and answers the call that marks that ranking
 * finished.
 *
 * WHY the answer acts once
 *   It runs from a `finally`; a second call must not finish a ranking another
 *   caller started, or the slot would stop showing as ranking while one still runs.
 */
function startSlotRanking(conversationId: string, anchorBookmark: string): () => void {
    const key = slotKeyOf(conversationId, anchorBookmark);
    /**
     * EXAMPLE
     *   no ranking under way -> 0 + 1 = 1; one already under way -> 1 + 1 = 2
     */
    slotRankingsUnderWay.set(key, (slotRankingsUnderWay.get(key) ?? 0) + 1);
    announceSlotRankingChange();
    let finished = false;
    return () => {
        /** WHEN this ranking was already marked finished CONSEQUENCE nothing changes */
        if (finished) return;
        finished = true;
        /**
         * EXAMPLE
         *   two rankings under way, this one finishes -> 2 - 1 = 1: the slot still shows as ranking
         *   one under way, this one finishes          -> 1 - 1 = 0: it no longer does
         */
        const stillUnderWay = (slotRankingsUnderWay.get(key) ?? 1) - 1;
        /** WHEN no other ranking of the slot is under way CONSEQUENCE the slot is dropped, so it reads as not ranking */
        if (stillUnderWay <= 0) slotRankingsUnderWay.delete(key);
        else slotRankingsUnderWay.set(key, stillUnderWay);
        announceSlotRankingChange();
    };
}

/** Registers a listener told whenever a slot's ranking starts or finishes or a rank is stored; answers the call that removes it. */
export function subscribeToSlotRanking(listener: () => void): () => void {
    slotRankingListeners.add(listener);
    return () => {
        slotRankingListeners.delete(listener);
    };
}

/** How many ranking changes the listeners have been told of so far. */
export function slotRankingChangeCountNow(): number {
    return slotRankingChangeCount;
}

/** True while the order step is ranking cards in the anchor's slot of the conversation. */
export function isSlotBeingRanked(conversationId: string, anchorBookmark: string): boolean {
    return slotRankingsUnderWay.has(slotKeyOf(conversationId, anchorBookmark));
}

/**
 * A slot paragraph's text with every tracked change accepted, read from the
 * export of its content range.
 *
 * NULL
 *   no export, for a paragraph that holds no characters: its text is "".
 *
 * EXAMPLE
 *   export of "Mixed Insertion" in a table cell -> ["Mixed Insertion"] -> "Mixed Insertion"
 *   null (an empty cell)                        -> ""
 */
function acceptedTextOfSlotParagraph(contentOoxml: OfficeExtension.ClientResult<string> | null): string {
    if (contentOoxml === null) return "";
    return new OoxmlRange(contentOoxml.value).allChangesAcceptedTexts()[0] ?? "";
}

const cardIdOf = (turn: ConversationMessage): string => turn.turnArtifacts?.tool_call_id?.trim() || turn.id || "";

/**
 * True for a turn that is a card's result: an assistant tool result of a tool
 * that lands paragraphs, not a sub-agent's forwarded tool call and not an error.
 */
const isCardResult = (turn: ConversationMessage): boolean => {
    const artifacts = turn.turnArtifacts;
    if (turn.turnType !== "assistant" || artifacts?.type !== "tool_result") return false;
    if (artifacts.wrapped_type !== undefined && artifacts.wrapped_type !== "tool_result") return false;
    return artifacts.tool_name !== undefined && LANDING_TOOL_NAMES.has(artifacts.tool_name) && !isToolErrorResult(turn.content);
};

/**
 * Why a card was proposed: the instructions its tool result carries.
 *
 * FALLBACK
 *   "" when the result carries none or is not JSON; the order step then reads the paragraphs alone.
 */
const intentOf = (turn: ConversationMessage): string => {
    try {
        const parsed = regenerateContextSchema.safeParse(JSON.parse(turn.content));
        return parsed.success ? parsed.data.regenerate_context.instructions : "";
    } catch {
        return "";
    }
};

/**
 * The live page as `InsertionSlot` reads it.
 *
 * followingParagraphsOf
 *   CASES
 *     - the anchor bookmark is gone            -> no paragraphs
 *     - the document ends inside the count     -> the paragraphs up to its end
 *   EXAMPLE
 *     paragraphCount = 2 -> the two paragraphs after the anchor's last paragraph
 *   WHY each paragraph's content range is exported, not the paragraph
 *     Word exports a table cell's paragraph as its whole row, so the first
 *     paragraph of that package is the row's first cell: every cell of
 *     "Service | Unit | Rate" read as "Service", and a new table of two or more
 *     columns never matched its own block. The content range exports the one
 *     paragraph alone, in a cell and in the body alike.
 *
 * nameParagraph
 *   Inserts a fresh paragraph bookmark on the paragraph's content, the name the
 *   naming pass would give it, so the apply can read a range that ends on it.
 */
export const officeSlotPage: SlotPage = {
    followingParagraphsOf: (anchorBookmark, paragraphCount) => withWordContextRead(async (context) => {
        const anchorRange = context.document.getBookmarkRangeOrNullObject(anchorBookmark);
        anchorRange.load("isNullObject");
        await context.sync();
        if (anchorRange.isNullObject) return [];
        const queued: Word.Paragraph[] = [];
        let paragraph = anchorRange.paragraphs.getLast();
        for (let step = 0; step < paragraphCount; step += 1) {
            paragraph = paragraph.getNextOrNullObject();
            paragraph.load("isNullObject,uniqueLocalId,text");
            queued.push(paragraph);
        }
        await context.sync();
        /**
         * EXAMPLE
         *   queued = [p1, p2(null)] -> firstMissingIndex = 1 -> slice(0, 1) = [p1]
         *   queued = [p1, p2]       -> firstMissingIndex = -1 -> every queued paragraph exists
         */
        const firstMissingIndex = queued.findIndex((candidate) => candidate.isNullObject);
        const existing = firstMissingIndex === -1 ? queued : queued.slice(0, firstMissingIndex);
        /**
         * CASES
         *   - the paragraph holds characters -> its content range is exported and read
         *   - it holds none                  -> no export: Word refuses to export an
         *                                       empty range (GeneralException) and the
         *                                       whole read would fail with it
         */
        const ooxmls = existing.map((candidate) => (candidate.text === "" ? null : candidate.getRange(Word.RangeLocation.content).getOoxml()));
        const bookmarkNames = existing.map((candidate) => candidate.getRange(Word.RangeLocation.whole).getBookmarks(true, false));
        await context.sync();
        return existing.map((candidate, index): SlotParagraph => ({
            uniqueLocalId: candidate.uniqueLocalId,
            acceptedText: acceptedTextOfSlotParagraph(ooxmls[index]),
            paragraphBookmarkName: bookmarkNames[index].value.find(isParagraphBookmarkName) ?? null,
        }));
    }),
    nameParagraph: (uniqueLocalId) => withWordContextWrite(async (context) => {
        const paragraph = context.document.getParagraphByUniqueLocalId(uniqueLocalId);
        const namesInUse = context.document.body.getRange(Word.RangeLocation.whole).getBookmarks(true, false);
        await context.sync();
        const bookmarkName = freshParagraphBookmarkName(new Set(namesInUse.value));
        paragraph.getRange(Word.RangeLocation.content).insertBookmark(bookmarkName);
        await context.sync();
        return bookmarkName;
    }),
    nameParagraphAfterTable: (cellParagraphUniqueLocalId) => withWordContextWrite(async (context) => {
        const cellParagraph = context.document.getParagraphByUniqueLocalId(cellParagraphUniqueLocalId);
        const table = await tableHolding(context, cellParagraph);
        const namesInUse = context.document.body.getRange(Word.RangeLocation.whole).getBookmarks(true, false);
        await context.sync();
        const bookmarkName = freshParagraphBookmarkName(new Set(namesInUse.value));
        /**
         * CASES
         *   - the paragraph sits in a table -> a paragraph is put after that table and named, the
         *                                      same step `TableChangeWriter` takes to place a table
         *                                      after a table; the card's own paragraph then follows
         *                                      it, outside the table
         *   - it is body text after all     -> it is named itself, as `nameParagraph` would
         */
        const paragraphToName = table === null ? cellParagraph : table.insertParagraph("", Word.InsertLocation.after);
        paragraphToName.getRange(Word.RangeLocation.content).insertBookmark(bookmarkName);
        await context.sync();
        return bookmarkName;
    }),
};

/**
 * The table a paragraph sits in, or null for a body paragraph.
 *
 * WHY the table it sits in, not the outermost one
 *   The paragraph being named is a cell of a table the slot itself added, and a
 *   slot can live inside a cell. Climbing to the outermost table would write what
 *   follows outside that cell, away from the slot it belongs to.
 *
 * NULL
 *   The paragraph is body text outside any table.
 */
async function tableHolding(context: Word.RequestContext, paragraph: Word.Paragraph): Promise<Word.Table | null> {
    const table = paragraph.parentTableOrNullObject;
    table.load("isNullObject");
    await context.sync();
    return table.isNullObject ? null : table;
}

/** A card found in the turns before its call index is known, and the place of its tool call among the turns. */
interface SlotCardAtCallTurn {
    cardWithoutCallIndex: Omit<SlotCard, "callIndex">;
    callTurnIndex: number;
}

/**
 * The index among the turns of each tool call's own turn, by tool call id: the
 * order the agent made its calls in.
 *
 * WHY the call turns and not the result turns
 *   Calls the agent makes together run in parallel, and each result turn is added
 *   when its result comes back, so the results stand in the order they finished.
 *   The call turns are added as the agent's message streams its tool calls, in the
 *   order it wrote them: "utilities first" was called before "right of use", but
 *   its result came back second, and the slot ranked "right of use" first (R39,
 *   -ba's QA step 4 item 11.4, 2026-10-16).
 *
 * EXAMPLE
 *   turns [user, call U, call R, result R, result U] -> { U: 1, R: 2 }
 */
function callTurnIndexByCardIdOf(turns: ConversationMessage[]): Map<string, number> {
    const callTurnIndexByCardId = new Map<string, number>();
    turns.forEach((turn, turnIndex) => {
        const toolCallId = turn.turnArtifacts?.tool_call_id?.trim();
        /** WHEN the turn is no tool call, or a call already seen (a call's turn is written once, its first sighting kept) CONSEQUENCE skipped */
        if (turn.turnArtifacts?.type !== "tool_call" || toolCallId === undefined || toolCallId === "" || callTurnIndexByCardId.has(toolCallId)) return;
        callTurnIndexByCardId.set(toolCallId, turnIndex);
    });
    return callTurnIndexByCardId;
}

/**
 * Every card of the conversation that adds paragraphs, in the order the agent
 * called them: a text card's insertions grouped by anchor, and a paragraph card's
 * new paragraphs after the paragraph they follow. Comments, replies, markup
 * redlines (whose paragraphs are not known as text) and failed results add none.
 *
 * WHY the order of the calls
 *   The call index is the order the slot falls back on, and the order the cards
 *   are put to the order step in; read off the result turns it was the order the
 *   results came back in (`callTurnIndexByCardIdOf`).
 *
 * FALLBACK
 *   A card whose call turn is not among the turns is placed by its result turn.
 *
 * EXAMPLE
 *   turns: [user, call A, result A (inserts "1.4" after _p13), user, result P (add_paragraph "1.5" after _p13)]
 *   -> [{ A, agentTurnNumber 1, callIndex 0, [{ _p13, ["1.4"] }] }, { P, agentTurnNumber 2, callIndex 1, [{ _p13, ["1.5"] }] }]
 *   turns: [user, call U, call R, result R, result U] -> [U callIndex 0, R callIndex 1]
 */
export function slotCardsOf(turns: ConversationMessage[]): SlotCard[] {
    const callTurnIndexByCardId = callTurnIndexByCardIdOf(turns);
    let userTurnsSoFar = 0;
    const cardsAtCallTurns: SlotCardAtCallTurn[] = [];
    turns.forEach((turn, turnIndex) => {
        if (turn.turnType === "user") userTurnsSoFar += 1;
        if (!isCardResult(turn)) return;
        const content = proposalCardContentOf(turn.turnArtifacts.tool_name, turn.content, undefined);
        const insertions = content === null ? [] : slotCardInsertionsOf(content);
        /** WHEN the card adds no block it can be found by CONSEQUENCE it is in no slot */
        if (insertions.length === 0) return;
        const cardId = cardIdOf(turn);
        /**
         * CASES
         *   - the card's call turn is among the turns -> its index, the order the agent called it in
         *   - it is not                               -> its result turn's index
         */
        const callTurnIndex = callTurnIndexByCardId.get(cardId) ?? turnIndex;
        cardsAtCallTurns.push({ cardWithoutCallIndex: { cardId, agentTurnNumber: userTurnsSoFar, intent: intentOf(turn), insertions }, callTurnIndex });
    });
    /**
     * EXAMPLE
     *   the third card the agent called that adds a block -> callIndex 2, counting from zero
     */
    return cardsAtCallTurns
        .sort((first, second) => first.callTurnIndex - second.callTurnIndex)
        .map(({ cardWithoutCallIndex }, callIndex) => ({ ...cardWithoutCallIndex, callIndex }));
}

/**
 * A card's new paragraphs grouped by the anchor they follow, its own order kept.
 *
 * CASES
 *   - a paragraph card     -> its new paragraphs after the paragraph it names
 *   - a text card          -> its insertions, grouped by `afterBookmark`
 *   - anything else        -> none
 */
function slotCardInsertionsOf(content: NonNullable<ReturnType<typeof proposalCardContentOf>>): SlotCardInsertion[] {
    if (content.kind === "paragraph") {
        /** WHEN the markup holds no paragraph with words CONSEQUENCE nothing to find on the page, so no slot */
        if (content.newParagraphTexts.length === 0) return [];
        /**
         * The paragraph the new ones follow, as the order step reads it.
         *
         * CASES
         *   - the result carries the section's markup        -> its last paragraph (`precedingParagraphText`)
         *   - a text card names the anchor among its paragraphs -> that paragraph as proposed
         *   - neither                                        -> "": the step orders on the paragraphs alone
         *
         * WHY the text card's paragraph
         *   A text `add_paragraph` result carries no section markup, so the step was asked
         *   with an empty anchor and ordered "right of use" and "utilities" without the
         *   paragraph granting the use they both follow (R39, 2026-10-16).
         */
        const anchorParagraphOfTextCard = content.textRedline?.paragraphs.find((paragraph) => paragraph.bookmark === content.precedingParagraphBookmark);
        const anchorText = content.precedingParagraphText ?? anchorParagraphOfTextCard?.textWhenProposed ?? "";
        return [{ anchorBookmark: content.precedingParagraphBookmark, anchorText, paragraphTexts: content.newParagraphTexts, tableRows: [] }];
    }
    if (content.kind === "comment" || content.kind === "reply" || content.textRedline === null) return [];
    const textRedline = content.textRedline;
    const byAnchor = new Map<string, SlotCardInsertion>();
    const entryAfter = (anchorBookmark: string): SlotCardInsertion => {
        const existing = byAnchor.get(anchorBookmark);
        if (existing !== undefined) return existing;
        const anchorParagraph = textRedline.paragraphs.find((paragraph) => paragraph.bookmark === anchorBookmark);
        const entry: SlotCardInsertion = { anchorBookmark, anchorText: anchorParagraph?.textWhenProposed ?? "", paragraphTexts: [], tableRows: [] };
        byAnchor.set(anchorBookmark, entry);
        return entry;
    };
    for (const insertion of textRedline.insertions) entryAfter(insertion.afterBookmark).paragraphTexts.push(insertion.textProposed);
    /**
     * WHY the tables
     *   A new table names the paragraph it follows exactly as a new paragraph does
     *   (`DesiredTableInsertion.afterBookmark`). Left out of the slot it was never
     *   ordered against the paragraphs landing after the same anchor, so it was
     *   written straight under the anchor, above all of them.
     */
    for (const tableInsertion of textRedline.tableChanges.tableInsertions) entryAfter(tableInsertion.afterBookmark).tableRows.push(tableInsertion.rows);
    return Array.from(byAnchor.values());
}

/**
 * Every rank the turns carry, by card and anchor, merged with the ranks the
 * order step returned in this session.
 *
 * WHY the session's ranks
 *   The order step stores a rank on the card's turn, but the turns loaded before
 *   it do not show it until the conversation is read again.
 */
export function slotRanksOf(conversationId: string, turns: ConversationMessage[]): Map<string, Map<string, StoredSlotRank>> {
    const ranks = new Map<string, Map<string, StoredSlotRank>>();
    for (const turn of turns) {
        if (!isCardResult(turn)) continue;
        const stored = storedSlotRanksSchema.safeParse((turn.turnArtifacts as Record<string, unknown>)[INSERTION_SLOT_RANKS_KEY]);
        if (stored.success) ranks.set(cardIdOf(turn), new Map(Object.entries(stored.data)));
    }
    for (const [cardId, byAnchor] of ranksOrderedThisSession.get(conversationId) ?? new Map<string, Map<string, StoredSlotRank>>()) {
        ranks.set(cardId, new Map([...(ranks.get(cardId) ?? new Map<string, StoredSlotRank>()), ...byAnchor]));
    }
    return ranks;
}

/**
 * The slot insertions of the given anchors: every paragraph each card of those
 * slots adds there, with the card's rank in the slot.
 *
 * NULL
 *   A card not ranked in the slot gets +Infinity: it sorts after every ranked card, the end of the slot.
 */
export function slotInsertionsOf(cards: SlotCard[], ranks: Map<string, Map<string, StoredSlotRank>>, anchorBookmarks: string[]): SlotInsertion[] {
    return cards.flatMap((card) => card.insertions
        .filter((insertion) => anchorBookmarks.includes(insertion.anchorBookmark))
        .flatMap((insertion) => {
            /**
             * NULL
             *   null when the card's turn holds no rank for this anchor: the order step has
             *   not settled it, and the card's call index orders it against the others.
             */
            const rank = ranks.get(card.cardId)?.get(insertion.anchorBookmark)?.rank ?? null;
            const inSlot = { cardId: card.cardId, order: { rank, callIndex: card.callIndex }, afterBookmark: insertion.anchorBookmark };
            return [
                ...insertion.paragraphTexts.map((textProposed): SlotInsertion => ({ ...inSlot, block: { kind: "paragraph", textProposed } })),
                /**
                 * EXAMPLE
                 *   rows [["Mixed Insertion", "Step 2 (table)"], ["Order", "2"]]
                 *   -> cellTexts ["Mixed Insertion", "Step 2 (table)", "Order", "2"], the paragraphs the walk reports
                 */
                ...insertion.tableRows.map((rows): SlotInsertion => ({ ...inSlot, block: { kind: "table", cellTexts: rows.flat() } })),
            ];
        }));
}

/** The cells of one table row as one line, for the order step to read the table as text. */
const TABLE_ROW_CELL_SEPARATOR = " | ";

/**
 * What the order step reads of a card's blocks in one slot: its new paragraphs,
 * then a line per row of each new table.
 *
 * WHY a table reads as lines
 *   The step decides where a block goes from what it says, and a table says it in
 *   its cells; a card that sent nothing would be ordered on its instructions alone.
 *
 * EXAMPLE
 *   paragraphTexts ["1.5 Late payments accrue interest."]
 *   tableRows      [[["Component", "Unit"], ["Platform", "per year"]]]
 *   -> ["1.5 Late payments accrue interest.", "Component | Unit", "Platform | per year"]
 */
function orderStepTextsOf(insertion: SlotCardInsertion): string[] {
    return [...insertion.paragraphTexts, ...insertion.tableRows.flatMap((rows) => rows.map((cellTexts) => cellTexts.join(TABLE_ROW_CELL_SEPARATOR)))];
}

/**
 * Every unranked card of one slot ordered by the core's order step, one at a
 * time, each against the cards ranked before it.
 *
 * CASES
 *   - the slot holds one card                -> nothing to order
 *   - every card of the slot is ranked       -> nothing to order, the page is not read
 *   - a card's order step cannot be reached  -> reported; the card stays unranked, the end of
 *                                               the slot, and the rest are still ordered
 *
 * EXAMPLE
 *   slot _p13: A (ranked 1.0), C (proposed first, toolu_02), B (proposed second, toolu_01)
 *   -> C ordered against [A], then B against [A, C] in their ranks
 *
 * WHILE the cards are ranked
 *   the slot reads as being ranked (`isSlotBeingRanked`), and stops when the
 *   last card is ranked or the ranking throws.
 */
async function orderSlot(input: SlotOrderingInput, anchorBookmark: string): Promise<void> {
    const slotCards = slotCardsOf(input.turns).flatMap((card) => {
        const insertion = card.insertions.find((candidate) => candidate.anchorBookmark === anchorBookmark);
        return insertion === undefined ? [] : [{ card, insertion }];
    });
    /** WHEN the card is alone in its slot CONSEQUENCE there is no order to settle */
    if (slotCards.length < 2) return;
    const ranks = slotRanksOf(input.conversationId, input.turns);
    const rankInSlotOf = (card: SlotCard): number | undefined => ranks.get(card.cardId)?.get(anchorBookmark)?.rank;
    const rankedSoFar = slotCards.flatMap((slotCard) => {
        const rank = rankInSlotOf(slotCard.card);
        return rank === undefined ? [] : [{ ...slotCard, rank }];
    });
    /**
     * WHY the order the agent proposed them
     *   Each card is ranked against the cards ranked before it, so a card ranked
     *   first sees none of the cards proposed with it. Tool call ids are random:
     *   sorted by id, a turn's last card ("Third") could be ranked first, against
     *   the slot's older blocks alone, and land after all of them, away from the
     *   "First" and "Second" it follows.
     *
     * EXAMPLE
     *   Third (call 7, toolu_01D), First (call 5, toolu_01T), Second (call 6, toolu_01W)
     *   -> First, Second, Third
     */
    const unranked = slotCards
        .filter((slotCard) => rankInSlotOf(slotCard.card) === undefined)
        .sort((first, second) => first.card.callIndex - second.card.callIndex);
    /** WHEN every card of the slot is ranked CONSEQUENCE nothing to order and the page is not read */
    if (unranked.length === 0) return;
    const finishRanking = startSlotRanking(input.conversationId, anchorBookmark);
    /**
     * WHY the ranking is finished in a `finally`
     *   The page read or the order step can throw; a slot left marked would show
     *   its cards as waiting on the ranking for as long as the session lasts.
     */
    try {
        const nextParagraphText = await nextParagraphTextOf(input, anchorBookmark, slotCards.map((slotCard) => slotCard.card), ranks);
        for (const { card, insertion } of unranked) {
            const members = [...rankedSoFar].sort((first, second) => first.rank - second.rank);
            try {
                const placement = await input.orderInsertion({
                    conversation_id: input.conversationId,
                    card_id: card.cardId,
                    anchor_bookmark: anchorBookmark,
                    anchor_text: insertion.anchorText,
                    next_paragraph_text: nextParagraphText,
                    members: members.map((member) => ({ card_id: member.card.cardId, rank: member.rank, paragraph_texts: orderStepTextsOf(member.insertion), intent: member.card.intent })),
                    arriving: { paragraph_texts: orderStepTextsOf(insertion), intent: card.intent },
                });
                rememberRank(input.conversationId, card.cardId, anchorBookmark, { rank: placement.rank, reason: placement.reason });
                rankedSoFar.push({ card, insertion, rank: placement.rank });
            } catch (failure) {
                input.reportFailure(card.cardId, failure);
            }
        }
    } finally {
        finishRanking();
    }
}

/**
 * The paragraph the document holds after the anchor before any card's paragraph:
 * the first paragraph under the anchor that holds no slot card's words.
 *
 * NULL
 *   null when the anchor is gone or ends the document.
 *
 * EXAMPLE
 *   page under _p13: "1.4 Fees." (card A, applied), "2. Term"; slot cards A, B
 *   -> read 3 paragraphs, 1 member -> following[1] = "2. Term"
 */
async function nextParagraphTextOf(input: SlotOrderingInput, anchorBookmark: string, slotCards: SlotCard[], ranks: Map<string, Map<string, StoredSlotRank>>): Promise<string | null> {
    const slotInsertions = slotInsertionsOf(slotCards, ranks, [anchorBookmark]);
    /**
     * EXAMPLE
     *   two new paragraphs and a table of four cells -> read 1 + 1 + 4 + 1 = 7 paragraphs:
     *   every block of the slot could be on the page, then the one that follows them
     */
    const following = await input.page.followingParagraphsOf(anchorBookmark, InsertionSlot.paragraphBudgetOf(slotInsertions));
    const members = InsertionSlot.membersOf(following, slotInsertions);
    /**
     * EXAMPLE
     *   members a paragraph and a four-cell table -> 1 + 4 = 5 paragraphs of the page taken,
     *   so following[5] is the first the slot does not hold
     */
    const paragraphsTheSlotHolds = members.reduce((count, member) => count + member.paragraphCount, 0);
    return following[paragraphsTheSlotHolds]?.acceptedText ?? null;
}

/** Keeps a rank the order step returned for the session, and tells the listeners, whose cards may now land elsewhere. */
function rememberRank(conversationId: string, cardId: string, anchorBookmark: string, rank: StoredSlotRank): void {
    const byCard = ranksOrderedThisSession.get(conversationId) ?? new Map<string, Map<string, StoredSlotRank>>();
    const byAnchor = byCard.get(cardId) ?? new Map<string, StoredSlotRank>();
    byAnchor.set(anchorBookmark, rank);
    byCard.set(cardId, byAnchor);
    ranksOrderedThisSession.set(conversationId, byCard);
    announceSlotRankingChange();
}

/**
 * The slot insertions of the given anchors, every unranked card of those slots
 * ordered first where the order step can be reached, and the ranks the
 * conversation already holds where it cannot. It never fails: a write is placed
 * either way.
 *
 * CASES
 *   - a signed-in user, the order step answers  -> the slots ordered, then read
 *   - no signed-in user                         -> reported; the ranks the turns hold, and the
 *                                                  cards' call order for the rest
 *   - reading the page or ordering a slot fails -> reported; the same
 *
 * WHY here and not in each apply
 *   Single Apply and Apply All need the same answer; the fallback written twice
 *   had already drifted (one threw, one returned early).
 */
export async function orderedSlotInsertionsOf(input: SlotsToOrder): Promise<SlotInsertion[]> {
    const anchorBookmarks = Array.from(new Set(input.anchorBookmarks));
    const orderInsertion = input.orderInsertion;
    if (orderInsertion === null) input.reportFailure(SLOT_ORDERING_FAILURE_CARD_ID, new Error("No signed-in user to order the slots; they are placed by the ranks the conversation holds"));
    else await orderSlotsReportingFailure({ ...input, orderInsertion }, anchorBookmarks);
    return slotInsertionsOf(slotCardsOf(input.turns), slotRanksOf(input.conversationId, input.turns), anchorBookmarks);
}

/**
 * Every unranked card of the given slots ordered, one slot at a time.
 * Orderings of one slot never overlap: a second apply waits for the first's
 * ordering and then finds the slot ranked.
 *
 * WHEN reading the page or ordering one slot throws
 *   CONSEQUENCE
 *     that slot is reported and keeps the ranks its turns already hold; the slots
 *     after it are still ordered. One slot never blocks the run.
 */
async function orderSlotsReportingFailure(input: SlotOrderingInput, anchorBookmarks: string[]): Promise<void> {
    for (const anchorBookmark of anchorBookmarks) {
        const key = slotKeyOf(input.conversationId, anchorBookmark);
        const previous = slotOrderingsInFlight.get(key) ?? Promise.resolve();
        const ordering = previous.then(() => orderSlot(input, anchorBookmark));
        slotOrderingsInFlight.set(key, ordering.catch(() => undefined));
        try {
            await ordering;
        } catch (failure) {
            input.reportFailure(SLOT_ORDERING_FAILURE_CARD_ID, failure);
        }
    }
}

/**
 * The anchors of the conversation's shared slots that still hold a card the
 * order step has not ranked: what is left to order once a turn's cards land.
 *
 * EXAMPLE
 *   _p13: A ranked, B unranked; _p20: C alone; _p31: D, E ranked -> ["_p13"]
 */
export function slotsAwaitingOrderOf(conversationId: string, turns: ConversationMessage[]): string[] {
    const ranks = slotRanksOf(conversationId, turns);
    const cardIdsByAnchor = new Map<string, string[]>();
    for (const card of slotCardsOf(turns)) {
        for (const insertion of card.insertions) cardIdsByAnchor.set(insertion.anchorBookmark, [...(cardIdsByAnchor.get(insertion.anchorBookmark) ?? []), card.cardId]);
    }
    return Array.from(cardIdsByAnchor.entries())
        .filter(([anchorBookmark, cardIds]) => cardIds.length >= 2 && cardIds.some((cardId) => ranks.get(cardId)?.get(anchorBookmark) === undefined))
        .map(([anchorBookmark]) => anchorBookmark);
}

/** A card whose block would go directly under the anchor it was proposed after. */
export interface SlotLandingUnderAnchor {
    kind: "anchor";
}

/** A card whose block would follow another card's new paragraph, as the page reads it. */
export interface SlotLandingAfterParagraph {
    kind: "paragraph";
    text: string;
}

/** A card whose block would follow another card's new table, named by its cells in reading order. */
export interface SlotLandingAfterTable {
    kind: "table";
    cellTexts: string[];
}

/** Where a card's new block would land if the card were applied now. */
export type SlotLanding = SlotLandingUnderAnchor | SlotLandingAfterParagraph | SlotLandingAfterTable;

/** One shared slot whose cards are read: its anchor, its cards, the conversation's ranks, which of its cards are applied, and the page. */
export interface SlotReadingsInput {
    anchorBookmark: string;
    cards: SlotCard[];
    ranks: Map<string, Map<string, StoredSlotRank>>;
    isCardApplied: (cardId: string) => boolean;
    page: SlotPage;
}

/**
 * What one read of a shared slot says of one of its cards: the block its "After"
 * line names, and where Go to issue takes the user, so the two never disagree.
 */
export interface SlotCardReading {
    landing: SlotLanding;
    place: SlotPlace;
    /** Whether the card was applied when its slot was read: the place is its own blocks then, the block it would follow before. */
    cardWasApplied: boolean;
}

/**
 * The conversation's slots that two or more cards add a block to, by anchor,
 * each with its cards in the order the agent proposed them: the slots whose
 * cards can land somewhere other than directly under their anchor.
 *
 * EXAMPLE
 *   A and B after _p13, C alone after _p20 -> { _p13: [A, B] }
 */
export function sharedSlotsOf(cards: SlotCard[]): Map<string, SlotCard[]> {
    const cardsByAnchor = new Map<string, SlotCard[]>();
    for (const card of cards) {
        for (const insertion of card.insertions) cardsByAnchor.set(insertion.anchorBookmark, [...(cardsByAnchor.get(insertion.anchorBookmark) ?? []), card]);
    }
    return new Map(Array.from(cardsByAnchor.entries()).filter(([, slotCards]) => slotCards.length >= 2));
}

/**
 * Every card of one shared slot as the page reads it now, by card id: where it
 * would land if applied, and where Go to issue takes the user. Read from the
 * page once for the whole slot, with nothing written to it.
 *
 * WHY one read for the slot
 *   Every card of the slot reads the same paragraphs under the anchor; a read
 *   per card would cost a Word round trip each and could see the page move
 *   between them.
 *
 * WHY Go to issue is read here
 *   A slot read exports each paragraph under the anchor, 0.45 s warm and 2.4 s
 *   cold for seven paragraphs (measured 2026-10-15, Word for Mac). Kept from the
 *   read the "After" line already makes, Go to issue only selects, one Word
 *   round trip of about 0.12 s.
 *
 * EXAMPLE
 *   slot after 10.1: First (rank 0), Second (rank 0.5, applied), Third (rank 0.75)
 *   page under 10.1: "Second, the engagement manager…", "Mixed Insertion — Step 1"
 *   -> First: lands under the anchor, Go to the anchor
 *      Second: lands under the anchor, Go to "Second, the engagement manager…", its own paragraph
 *      Third: lands after "Second, the engagement manager…", Go to that paragraph
 */
export async function slotReadingsOf({ anchorBookmark, cards, ranks, isCardApplied, page }: SlotReadingsInput): Promise<Map<string, SlotCardReading>> {
    const slotInsertions = slotInsertionsOf(cards, ranks, [anchorBookmark]);
    const pageReadOnce = slotPageReadOnce(page);
    const readings = new Map<string, SlotCardReading>();
    for (const card of cards) {
        const order: CardInsertionOrder = { cardId: card.cardId, slotInsertions };
        const cardWasApplied = isCardApplied(card.cardId);
        const predecessor = await InsertionSlot.landingPredecessorOf({ anchorBookmark, order, page: pageReadOnce });
        const blocksShown = await slotBlocksShownFor({ anchorBookmark, order, cardIsApplied: cardWasApplied, pageReadOnce });
        readings.set(card.cardId, { landing: slotLandingOf(predecessor), place: slotPlaceOfBlocks(blocksShown), cardWasApplied });
    }
    return readings;
}

/**
 * A card's predecessor in its slot as the card shows it.
 *
 * CASES
 *   - nothing of the slot stands before the card -> the anchor
 *   - another card's new table                   -> that table's cells
 *   - another card's new paragraph               -> its words as the page reads them
 */
function slotLandingOf(predecessor: SlotMember | null): SlotLanding {
    if (predecessor === null) return { kind: "anchor" };
    if (predecessor.block.kind === "table") return { kind: "table", cellTexts: predecessor.block.cellTexts };
    return { kind: "paragraph", text: predecessor.lastParagraph.acceptedText };
}

/** Why a landing or place read refuses to name a paragraph: drawing a card, or going to it, never writes to the document. */
const LANDING_READ_NAMES_NOTHING = "A landing is read from the page; it names no paragraph";

/**
 * The page as a slot is read for its cards' landings and Go to issue places:
 * the paragraphs under each anchor read once and shared by every card of the
 * slot, and no paragraph ever named.
 *
 * WHY naming refuses instead of writing
 *   `landingPredecessorOf` and `membersOfCard` never name a paragraph, so a call
 *   here is a defect; it fails loudly rather than put a bookmark in the document
 *   while a card is drawn or gone to.
 */
function slotPageReadOnce(page: SlotPage): SlotPage {
    const readsByAnchorAndCount = new Map<string, Promise<SlotParagraph[]>>();
    return {
        followingParagraphsOf: (anchorBookmark, paragraphCount) => {
            const readKey = `${anchorBookmark}\u0000${paragraphCount}`;
            const read = readsByAnchorAndCount.get(readKey) ?? page.followingParagraphsOf(anchorBookmark, paragraphCount);
            readsByAnchorAndCount.set(readKey, read);
            return read;
        },
        nameParagraph: () => Promise.reject(new Error(LANDING_READ_NAMES_NOTHING)),
        nameParagraphAfterTable: () => Promise.reject(new Error(LANDING_READ_NAMES_NOTHING)),
    };
}

/** Go to issue takes the user to the card's anchor, as the card's bookmarks name it. */
export interface SlotPlaceAtAnchor {
    kind: "anchor";
}

/** Go to issue takes the user to blocks of the slot on the page: from the first one's first paragraph to the last one's last. */
export interface SlotPlaceOnBlocks {
    kind: "blocks";
    firstUniqueLocalId: string;
    lastUniqueLocalId: string;
}

/** Where Go to issue takes the user for a card that adds a block after an anchor. */
export type SlotPlace = SlotPlaceAtAnchor | SlotPlaceOnBlocks;

export const SLOT_PLACE_AT_ANCHOR: SlotPlaceAtAnchor = { kind: "anchor" };

/** A card of a slot whose place is read: the anchor its block follows, whether it is applied, the conversation's slot cards and ranks, and the page. */
export interface SlotPlaceInput {
    anchorBookmark: string;
    cardId: string;
    cardIsApplied: boolean;
    cards: SlotCard[];
    ranks: Map<string, Map<string, StoredSlotRank>>;
    page: SlotPage;
}

/**
 * Where Go to issue takes the user for a card that adds a block after an anchor,
 * read from the page with nothing written to it: the click-time read, for a card
 * whose slot the "After" lines did not read (a card alone after its anchor), or
 * read before the card was applied.
 *
 * CASES
 *   - applied, its blocks on the page                 -> those blocks: where it was written
 *   - applied, its blocks gone (changed or rejected)  -> the anchor
 *   - open, a block of its slot stands before it      -> that block: the one its "After" line names
 *   - open, nothing of its slot stands before it      -> the anchor, where it lands
 *
 * EXAMPLE
 *   page under 10.4: "Notice first" (A, applied), "First" (C, applied); B open, ranked between them
 *   -> A: "Notice first"; B: "Notice first", the block B would follow; C: "First"
 */
export async function slotPlaceOf({ anchorBookmark, cardId, cardIsApplied, cards, ranks, page }: SlotPlaceInput): Promise<SlotPlace> {
    const order: CardInsertionOrder = { cardId, slotInsertions: slotInsertionsOf(cards, ranks, [anchorBookmark]) };
    return slotPlaceOfBlocks(await slotBlocksShownFor({ anchorBookmark, order, cardIsApplied, pageReadOnce: slotPageReadOnce(page) }));
}

/** A card of a slot whose Go to issue blocks are read: its anchor, its order in the slot, whether it is applied, and the page read once. */
interface SlotBlocksShownInput {
    anchorBookmark: string;
    order: CardInsertionOrder;
    cardIsApplied: boolean;
    pageReadOnce: SlotPage;
}

/**
 * The blocks of the card's slot Go to issue shows.
 *
 * CASES
 *   - the card is applied -> its own blocks
 *   - it is open          -> the block it would follow, or none when it would go under the anchor
 */
async function slotBlocksShownFor({ anchorBookmark, order, cardIsApplied, pageReadOnce }: SlotBlocksShownInput): Promise<SlotMember[]> {
    if (cardIsApplied) return InsertionSlot.membersOfCard({ anchorBookmark, order, page: pageReadOnce });
    const predecessor = await InsertionSlot.landingPredecessorOf({ anchorBookmark, order, page: pageReadOnce });
    /** WHEN nothing of the slot stands before the open card CONSEQUENCE none: it lands under the anchor */
    return predecessor === null ? [] : [predecessor];
}

/**
 * The place that spans the blocks Go to issue shows.
 *
 * CASES
 *   - no block -> the anchor
 *   - blocks   -> from the first block's first paragraph to the last block's last
 *
 * EXAMPLE
 *   blocks [table T, paragraph P] -> T's first cell paragraph to P, blocks[1]
 */
function slotPlaceOfBlocks(blocks: SlotMember[]): SlotPlace {
    /** WHEN no block of the slot stands for the card CONSEQUENCE the anchor */
    if (blocks.length === 0) return SLOT_PLACE_AT_ANCHOR;
    return { kind: "blocks", firstUniqueLocalId: blocks[0].firstParagraph.uniqueLocalId, lastUniqueLocalId: blocks[blocks.length - 1].lastParagraph.uniqueLocalId };
}

/**
 * Selects blocks of a slot in Word, from the first paragraph to the last, as Go
 * to issue selects a card's bookmarked paragraphs, in one Word round trip.
 *
 * RETURNS
 *   false, selecting nothing, when either paragraph has left the document since
 *   the slot was read: the caller goes to the card's anchor instead.
 */
export const selectSlotPlaceInWord = ({ firstUniqueLocalId, lastUniqueLocalId }: SlotPlaceOnBlocks): Promise<boolean> => withWordContextWrite(async (context) => {
    const firstParagraph = context.document.getParagraphByUniqueLocalId(firstUniqueLocalId);
    const lastParagraph = context.document.getParagraphByUniqueLocalId(lastUniqueLocalId);
    firstParagraph.getRange(Word.RangeLocation.whole).expandTo(lastParagraph.getRange(Word.RangeLocation.whole)).select();
    try {
        await context.sync();
        return true;
    } catch (error) {
        /** WHEN Word no longer holds a paragraph of the place CONSEQUENCE nothing is selected; any other failure is thrown */
        if (error instanceof OfficeExtension.Error && error.code === Word.ErrorCodes.itemNotFound) return false;
        throw error;
    }
});

/**
 * Every anchor a card adds a block after: the paragraph each new paragraph
 * follows, and the paragraph each new table follows.
 *
 * EXAMPLE
 *   a card adding one new table after "1.3" -> ["_dioptra_p_13"], so the table is
 *   ordered against the paragraphs the other cards add there
 */
export const insertionAnchorsOfCard = (card: RedlineTextProposal): string[] => [
    ...card.insertions.map((insertion) => insertion.afterBookmark),
    ...card.tableChanges.tableInsertions.map((tableInsertion) => tableInsertion.afterBookmark),
];

/** Every anchor a run's text cards add a block after. */
export const insertionAnchorsOf = (remedies: LiveReviewBulkApplyRemedy[]): string[] =>
    remedies.flatMap((remedy) => (remedy.target_texts === null ? [] : insertionAnchorsOfCard(remedy.target_texts)));

/**
 * The remedies of an Apply All run with each text card's insertions placed in
 * its slot, and the cards that share a slot put in their rank order there, so
 * the fold lays their new paragraphs in that order (`withInsertionsInOneBuild`).
 * Every other remedy keeps its place.
 *
 * CASES
 *   - a remedy carries no text card, or no insertion -> unchanged, in its place
 *   - reading or naming its slot on the page fails    -> unchanged, reported; the run goes on with it
 *                                                        under its anchor, as one card never blocks a run
 *   - otherwise                                        -> placed
 *
 * EXAMPLE
 *   run [X (no insertion), C (slot _p13 rank 3), Y, B (slot _p13 rank 2)]
 *   -> [X, B, Y, C]: B and C swap the places the slot's cards hold
 */
export async function placeRemedyInsertionsInSlots(remedies: LiveReviewBulkApplyRemedy[], slotInsertions: SlotInsertion[], page: SlotPage, reportPlacementFailure: PlacementFailureReporter): Promise<LiveReviewBulkApplyRemedy[]> {
    const placed: LiveReviewBulkApplyRemedy[] = [];
    for (const remedy of remedies) {
        /** WHEN the remedy carries no text card CONSEQUENCE there is nothing to place */
        if (remedy.target_texts === null) {
            placed.push(remedy);
            continue;
        }
        try {
            const placedCard = await InsertionSlot.placeCardParagraphs({ card: remedy.target_texts, bookmarkNames: remedy.target_bookmark_names, order: { cardId: remedy.rule_uuid, slotInsertions }, page });
            placed.push({ ...remedy, target_texts: placedCard.card, target_bookmark_names: placedCard.bookmarkNames });
        } catch (failure) {
            reportPlacementFailure(remedy.rule_uuid, failure);
            placed.push(remedy);
        }
    }
    return inSlotRankOrder(placed, slotInsertions);
}

/** One card of a run whose tables the table step is about to write. */
export interface TableCardToPlace {
    cardId: string;
    card: RedlineTextProposal;
}

/**
 * The run's table cards with each new table placed in its slot, read from the
 * page as it stands now.
 *
 * WHY this is not done with the paragraphs
 *   A run writes its new paragraphs first and its tables in a step of their own,
 *   so at fold time a table that goes between two of the run's paragraphs has
 *   nothing on the page to follow. Read here, the paragraphs have landed and the
 *   table finds the one it follows.
 *
 * CASES
 *   - the card adds no table                   -> unchanged
 *   - reading or naming its slot fails          -> unchanged, reported; the run writes it under
 *                                                  its anchor, as one card never blocks a run
 *   - otherwise                                 -> placed
 */
export async function placeTableCardsInSlots(tableCards: TableCardToPlace[], slotInsertions: SlotInsertion[], page: SlotPage, reportPlacementFailure: PlacementFailureReporter): Promise<RedlineTextProposal[]> {
    const placed: RedlineTextProposal[] = [];
    for (const tableCard of tableCards) {
        try {
            placed.push(await InsertionSlot.placeCardTables({ card: tableCard.card, order: { cardId: tableCard.cardId, slotInsertions }, page }));
        } catch (failure) {
            reportPlacementFailure(tableCard.cardId, failure);
            placed.push(tableCard.card);
        }
    }
    return placed;
}

/**
 * The run with the remedies that share a slot reordered among the places they
 * hold, by their rank in that slot; a remedy is keyed by the slot of its first
 * insertion.
 *
 * EXAMPLE
 *   places 1 and 3 hold C (rank 3) and B (rank 2) of one slot -> place 1 B, place 3 C
 */
function inSlotRankOrder(remedies: LiveReviewBulkApplyRemedy[], slotInsertions: SlotInsertion[]): LiveReviewBulkApplyRemedy[] {
    const placesBySlot = new Map<string, PlaceInARun[]>();
    remedies.forEach((remedy, place) => {
        const inSlot = sharedSlotInsertionOf(remedy, slotInsertions);
        /** WHEN the remedy adds a block to no slot it shares CONSEQUENCE it keeps its place */
        if (inSlot === null) return;
        placesBySlot.set(inSlot.afterBookmark, [...(placesBySlot.get(inSlot.afterBookmark) ?? []), { place, order: inSlot.order }]);
    });
    const reordered = [...remedies];
    placesBySlot.forEach((places) => {
        const byOrder = [...places].sort((first, second) => compareSlotOrder(first.order, second.order));
        places.forEach(({ place }, index) => {
            reordered[place] = remedies[byOrder[index].place];
        });
    });
    return reordered;
}

/** One remedy of a run that adds a block to a shared slot: where it stands in the run, and where the slot puts it. */
interface PlaceInARun {
    place: number;
    order: SlotOrder;
}

/**
 * The remedy's first block in a slot another card shares, as the slot insertions
 * name it (its anchor before placement).
 *
 * NULL
 *   null when the remedy adds nothing, or no other card adds a block there.
 */
function sharedSlotInsertionOf(remedy: LiveReviewBulkApplyRemedy, slotInsertions: SlotInsertion[]): SlotInsertion | null {
    const inSlot = slotInsertions.find((slotInsertion) => slotInsertion.cardId === remedy.rule_uuid) ?? null;
    if (inSlot === null) return null;
    const sharesTheSlot = slotInsertions.some((slotInsertion) => slotInsertion.afterBookmark === inSlot.afterBookmark && slotInsertion.cardId !== remedy.rule_uuid);
    return sharesTheSlot ? inSlot : null;
}
